import React from 'react';
import { useTranslation } from 'react-i18next';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import _get from 'lodash/get';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import UserChip from '@lib/components/UserChip/UserChip';
import LinkComponent, { LinkTypes } from '@lib/components/Link/Link';
import SupportiveMessage from '@lib/components/SupportiveMessage/SupportiveMessage';
import classNames from 'classnames';
import Map from '@lib/components/Map/Map';
import { Sector } from 'graphql-common';

type Props = {
  data?: Sector | null;
  onEditHandler: () => void;
  isViewOnly: boolean;
};

export default function SectorDetails({
  data,
  isViewOnly,
  onEditHandler,
}: Props) {
  const { t } = useTranslation();
  const name = _get(data, 'name', '');
  const description = _get(data, 'description', '');
  const code = _get(data, 'code', '-');
  const color = colorToChipType(_get(data, 'color'), ChipTypes.chipWhite);
  const user = _get(data, 'user');
  const locationGeojson = _get(data, ['locationBoundary', 'geojson']);

  return (
    <div className={entityViewStyles.infoList}>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="share_location" symbolsOutlined />
          <Typography variant="caption">{t('sector-label')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">
            <Chip type={color} label={name} leftIcon="circle" />
          </Typography>
        </div>
      </div>
      <div className={entityViewStyles.infoItem}>
        <p className={entityViewStyles.infoItemLabel}>
          <MaterialIcon icon="password" />
          <Typography variant="caption">{t('sector-code')}</Typography>
        </p>
        <div className={entityViewStyles.infoItemContent}>
          <Typography variant="body">{code}</Typography>
        </div>
      </div>
      {hasTextEditorValue(description) && (
        <div
          className={classNames(
            entityViewStyles.infoItem,
            entityViewStyles.block,
          )}
        >
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="description" symbolsOutlined />
            <Typography variant="caption">{t('description')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <TextEditor
              readOnly
              value={description}
              className={entityViewStyles.textEditor}
            />
          </div>
        </div>
      )}
      <Typography
        variant="h3"
        className={classNames(
          entityViewStyles.infoListTitle,
          entityViewStyles.hasTopSpace,
        )}
      >
        {t('responsible-person')}
      </Typography>
      {user ? (
        <>
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="person" symbolsOutlined />
              <Typography variant="caption">{t('name')}</Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              {user ? (
                <UserChip user={user} />
              ) : (
                <Typography variant="body">-</Typography>
              )}
            </div>
          </div>
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="call" symbolsOutlined />
              <Typography variant="caption">{t('phone-number')}</Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              <Typography variant="body">{user?.phoneNumber || '-'}</Typography>
            </div>
          </div>
          <div className={entityViewStyles.infoItem}>
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="mail" symbolsOutlined />
              <Typography variant="caption">{t('email')}</Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              {user?.email ? (
                <LinkComponent
                  type={LinkTypes.secondaryGray}
                  data-test-id="sector-details__user-email-link"
                >
                  {user.email}
                </LinkComponent>
              ) : (
                <Typography variant="body">-</Typography>
              )}
            </div>
          </div>
        </>
      ) : (
        <SupportiveMessage
          text={t('no-sector-responsible-person')}
          actions={
            isViewOnly ? undefined : (
              <Button
                buttonText={t('edit')}
                buttonType={ButtonTypes.primaryFilled}
                buttonSize={ButtonSizes.small}
                onClick={onEditHandler}
                leftIcon="edit"
                leftIconClassName={materialSymbolsOutlined}
              />
            )
          }
        />
      )}
      <Typography
        variant="h3"
        className={classNames(
          entityViewStyles.infoListTitle,
          entityViewStyles.hasTopSpace,
        )}
      >
        {t('location-on-map')}
      </Typography>
      <Map value={locationGeojson} />
    </div>
  );
}
